import { Config, Constant } from '@genial/config';
import { Utils } from '@genial/utils';
import { InvalidTokenError } from 'jwt-decode';
import { MutableRefObject, useCallback, useRef } from 'react';
import { useRecoilState } from 'recoil';

import { useToastCreator } from '../../hooks/useToastCreator';

import { hasRefreshTokenObserver, sessionState } from './atom';
import { UserProfileLegacy } from './types';
import {
  buildSessionAtomFromLocalStorage,
  refreshTokenFromLocalStorage,
  sessionExpirationFromLocalStorage,
  updateSessionWithSignedInJsonToken,
} from './utils';

export const useSessionActions = () => {
  const tokenObserverTimeout: MutableRefObject<NodeJS.Timeout | null> = useRef(null);
  const isRefreshingToken = useRef(false);
  const { showToast } = useToastCreator('session');
  const [session, setSession] = useRecoilState(sessionState);
  const [refreshTokenObserver, setHasRefreshTokenObserver] =
    useRecoilState(hasRefreshTokenObserver);

  const clearRefreshTokenObserver = useCallback(() => {
    setHasRefreshTokenObserver(false);

    if (tokenObserverTimeout.current) {
      clearInterval(tokenObserverTimeout.current);
    }
  }, [setHasRefreshTokenObserver]);

  const refreshTokenFetcher = useCallback(async () => {
    const sessionFromLocalStorage = await buildSessionAtomFromLocalStorage();
    const currentRefreshToken = await refreshTokenFromLocalStorage();
    const response = await fetch(`${Config.OriginUrlApi}/User/refresh-token`, {
      method: 'POST',
      body: JSON.stringify({
        refreshToken: currentRefreshToken,
      }),
      headers: {
        'content-Type': 'application/json',
      },
      mode: 'cors',
      credentials: 'same-origin',
    });

    if (response.status === 201) {
      const json = await response.json();
      await updateSessionWithSignedInJsonToken(sessionFromLocalStorage, setSession, json);
    } else {
      throw new InvalidTokenError();
    }
  }, [setSession]);

  const createRefreshTokenObserver = useCallback(() => {
    clearRefreshTokenObserver();
    setHasRefreshTokenObserver(true);

    tokenObserverTimeout.current = setInterval(async () => {
      const tokenExpiresAt = await sessionExpirationFromLocalStorage();

      if (!tokenExpiresAt) {
        return;
      }

      if (tokenExpiresAt.getTime() - Date.now() < 120000) {
        if (isRefreshingToken.current) {
          return;
        }

        isRefreshingToken.current = true;

        refreshTokenFetcher()
          .catch((error) => {
            if (error instanceof InvalidTokenError) {
              signOut();
            }
          })
          .finally(() => {
            isRefreshingToken.current = false;
          });
      }
    }, 60000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHasRefreshTokenObserver]);

  const signIn = async (email: string, password: string, tfaToken: string = '') => {
    const defaultError = 'Ocorreu um erro inesperado.';
    let hasError = false;
    let errorMessage = defaultError;

    try {
      const response = await fetch(`${Config.OriginUrlApi}/User/authenticate`, {
        method: 'POST',
        body: JSON.stringify({
          login: email,
          password,
          tfaToken,
        }),
        headers: {
          'content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'same-origin',
      });

      if (response.status === 200) {
        const json = await response.json();
        const apiUrl = Utils.Normalize.ApiUrl(Config.GenialExtranetApiUrl);

        try {
          const userProfileRequest = await fetch(`${apiUrl}/api/usuario/ObterInformacoesUsuario`, {
            headers: {
              'content-Type': 'application/json',
              Authorization: `Bearer ${json.jwtToken}`,
            },
            mode: 'cors',
            credentials: 'same-origin',
          });

          if (userProfileRequest.status === 200) {
            const userProfile = (await userProfileRequest.json()) as UserProfileLegacy;

            await updateSessionWithSignedInJsonToken(
              {
                ...session,
                userProfile,
              },
              setSession,
              json
            );
          } else {
            hasError = true;
          }
        } catch (_error) {
          hasError = true;
        }
      } else {
        const body = await response.text();

        hasError = true;
        errorMessage = body;
      }
    } catch (_error) {
      hasError = true;
    }

    if (hasError) {
      showToast('error', 'Error', errorMessage);
    }
  };

  const signOut = async () => {
    window.dtrum?.endSession();

    await Utils.Storage.RemoveItem(Constant.TokenKey);
    await Utils.Storage.RemoveItem(Constant.RefreshTokenKey);
    await Utils.Storage.RemoveItem(Constant.RefreshTokenKeyExpiration);
    await Utils.Storage.RemoveItem(Constant.UserProfileKey);

    clearRefreshTokenObserver();
    setSession({
      expiresAt: null,
      signedIn: false,
      token: null,
      refreshToken: null,
      refreshTokenExpiresAt: null,
      isMaster: false,
      userProfile: undefined,
    });
  };

  const recoverPasswordRequest = async (email: string = '', cpf: string = '') => {
    let hasError = false;
    const apiUrl = Utils.Normalize.ApiUrl(Config.GenialExtranetApiUrl);

    try {
      const response = await fetch(`${apiUrl}/api/Home/SolicitarRecuperacaoSenhaParceiros`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          cpf,
        }),
        headers: {
          'content-Type': 'application/json',
          'g-recaptcha-response': '123',
        },
        mode: 'cors',
        credentials: 'same-origin',
      });

      if (response.status !== 200) {
        hasError = true;
      }
    } catch (_error) {
      hasError = true;
    }

    if (hasError) {
      showToast(
        'error',
        'Ops! Ocorreu um erro interno',
        'Estamos trabalhando para resolvê-lo. Por favor, tente novamente mais tarde ou entre em contato com time de middle pelo e-mail middleb2b@genial.com.vc.'
      );

      throw new Error();
    }

    showToast(
      'success',
      'Tudo certo!',
      'Caso seu endereço de e-mail esteja cadastrado na PARCEIROS, você receberá um e-mail com as instruções para redefinir sua senha, Se você não receber o e-mail em alguns minutos, verifique a sua caixa de spam ou tente novamente.'
    );
  };

  const recoverPasswordReset = async (
    email: string,
    cpf: string,
    token: string,
    password: string,
    passwordConfirm: string
  ) => {
    const apiUrl = Utils.Normalize.ApiUrl(Config.GenialExtranetApiUrl);
    let hasError = false;
    let errorTitle = 'Ops! Ocorreu um erro interno';
    let errorMessage =
      'Tente novamente mais tarde ou entre em contato com time de middle pelo e-mail middleb2b@genial.com.vc.';

    try {
      const response = await fetch(`${apiUrl}/api/Home/ConfirmarRecuperacaoSenhaParceiros`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          cpf,
          novaSenha: password,
          confirmacaoNovaSenha: passwordConfirm,
          codigoEmail: token,
        }),
        headers: {
          'content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'same-origin',
      });

      if (response.status !== 200) {
        hasError = true;
      } else {
        const json = (await response.json()) as {
          sucesso: boolean;
          exception: { Message: string }[];
        };

        if (!json.sucesso) {
          errorTitle = 'Ops! Ocorreu um erro';
          errorMessage = json.exception[0].Message;
          hasError = true;
        }
      }
    } catch (_error) {
      hasError = true;
    }

    if (hasError) {
      showToast('error', errorTitle, errorMessage);
      throw new Error();
    }

    showToast('success', 'Tudo certo!', 'Sua senha foi alterada com sucesso.');
  };

  return {
    signIn,
    signOut,
    hasRefreshTokenObserver: refreshTokenObserver,
    createRefreshTokenObserver,
    clearRefreshTokenObserver,
    recoverPasswordRequest,
    recoverPasswordReset,
  };
};
